/**
 * features/hr/ContractsCenter.tsx
 * ------------------------------------------------------------------
 * HR DOMAIN — Contracts (school-wide).
 *
 * Three sections in priority order:
 *   1. ENDING SOON     active contracts with endDate within 90 days
 *                      — the actionable list
 *   2. ACTIVE          all other active contracts
 *   3. RECENTLY        contracts archived in the last 12 months
 *      ARCHIVED        — surfaces the renewal story from the
 *                      seed data
 *
 * Type filter chips along the top let HR narrow by contract type.
 * Clicking any row opens the staff profile (Contract & Payroll tab).
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import {
  FileSignature,
  AlertTriangle,
  FileText,
  Archive,
  CheckCircle2,
} from 'lucide-react';
import { GradientHero, SoftCard, StatChip } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { useHrData } from '@/features/hr/useHrData';
import StaffWorkspace from '@/features/hr/StaffWorkspace';
import {
  staffFullName,
  staffInitials,
  daysUntil,
  type ContractType,
  type HrContract,
  type HrStaff,
} from '@/lib/hr';

const TYPE_LABELS: Record<ContractType, string> = {
  permanent: 'Permanent',
  fixed_term: 'Fixed term',
  probation: 'Probation',
  casual: 'Casual',
};

type TypeFilter = 'all' | ContractType;

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

function endLabel(c: HrContract): string {
  if (!c.endDate) return 'no end date';
  const d = daysUntil(c.endDate);
  if (d === null) return 'no end date';
  if (d < 0) return `ended ${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} ago`;
  if (d === 0) return 'ends today';
  return `${d} day${d === 1 ? '' : 's'} left`;
}

interface Props {
  onOpenStaff: (staffId: string) => void;
}

export default function ContractsCenter({ onOpenStaff }: Props) {
  const data = useHrData();
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

  const enriched = useMemo(() => {
    return data.contracts
      .map((c) => ({ contract: c, staff: data.staffById(c.staffId) }))
      .filter(
        (r): r is { contract: HrContract; staff: HrStaff } => !!r.staff,
      )
      .filter((r) => typeFilter === 'all' || r.contract.type === typeFilter);
  }, [data.contracts, data.staffById, typeFilter]);

  const endingSoon = useMemo(() => {
    return enriched
      .filter((r) => r.contract.active)
      .filter((r) => {
        const d = daysUntil(r.contract.endDate);
        return d !== null && d >= 0 && d <= 90;
      })
      .sort(
        (a, b) =>
          (daysUntil(a.contract.endDate) ?? 0) -
          (daysUntil(b.contract.endDate) ?? 0),
      );
  }, [enriched]);

  const active = useMemo(() => {
    const endingSet = new Set(endingSoon.map((r) => r.contract.id));
    return enriched
      .filter((r) => r.contract.active && !endingSet.has(r.contract.id))
      .sort((a, b) =>
        staffFullName(a.staff).localeCompare(staffFullName(b.staff)),
      );
  }, [enriched, endingSoon]);

  const archived = useMemo(() => {
    const cutoff = Date.now() - 365 * 86400000;
    return enriched
      .filter((r) => !r.contract.active)
      .filter((r) => {
        if (!r.contract.endDate) return false;
        return new Date(r.contract.endDate).getTime() >= cutoff;
      })
      .sort((a, b) =>
        (b.contract.endDate ?? '').localeCompare(a.contract.endDate ?? ''),
      );
  }, [enriched]);

  const counts = useMemo(() => {
    let activeC = 0;
    let endingC = 0;
    let archivedC = 0;
    for (const r of enriched) {
      if (r.contract.active) {
        activeC += 1;
        const d = daysUntil(r.contract.endDate);
        if (d !== null && d >= 0 && d <= 90) endingC += 1;
      } else {
        archivedC += 1;
      }
    }
    return { active: activeC, ending: endingC, archived: archivedC };
  }, [enriched]);

  return (
    <div className="space-y-6">
      <GradientHero
        icon={FileSignature}
        title="Contracts"
        subtitle="Active contracts, renewals, and archived history"
      />

      {data.loading ? (
        <div className="flex justify-center py-16">
          <Spinner label="Loading contracts…" />
        </div>
      ) : (
        <>
          {/* Top-line counts */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatChip tone="emerald" icon={CheckCircle2} value={counts.active} label="Active" />
            <StatChip tone="gold" icon={AlertTriangle} value={counts.ending} label="Ending within 90 days" />
            <StatChip tone="slate" icon={Archive} value={counts.archived} label="Archived" />
            <StatChip tone="purple" value={enriched.length} label="Total" />
          </div>

          <StaffWorkspace mode="contract" onOpenStaff={onOpenStaff} />

          <details className="group">
            <summary className="cursor-pointer text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 select-none">
              Contract overview — ending soon, active &amp; archived
            </summary>
            <div className="space-y-6 mt-4">
          {/* Type filter */}
          <SoftCard hover={false}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 font-semibold w-12 shrink-0">
                Type
              </span>
              <div className="flex flex-wrap gap-1.5">
                <FilterChip active={typeFilter === 'all'} onClick={() => setTypeFilter('all')}>
                  All
                </FilterChip>
                {(Object.keys(TYPE_LABELS) as ContractType[]).map((t) => (
                  <FilterChip
                    key={t}
                    active={typeFilter === t}
                    onClick={() => setTypeFilter(t)}
                  >
                    {TYPE_LABELS[t]}
                  </FilterChip>
                ))}
              </div>
            </div>
          </SoftCard>

          {/* Ending soon */}
          {endingSoon.length > 0 && (
            <section className="space-y-2">
              <SectionHeader
                tone="amber"
                icon={AlertTriangle}
                label="Ending within 90 days"
                count={endingSoon.length}
              />
              <div className="space-y-2">
                {endingSoon.map(({ contract, staff }) => (
                  <ContractCard
                    key={contract.id}
                    contract={contract}
                    staff={staff}
                    onClick={() => onOpenStaff(staff.id)}
                    accent="amber"
                  />
                ))}
              </div>
            </section>
          )}

          {/* Active */}
          {active.length > 0 && (
            <section className="space-y-2">
              <SectionHeader
                tone="slate"
                icon={CheckCircle2}
                label="Active"
                count={active.length}
              />
              <div className="space-y-2">
                {active.map(({ contract, staff }) => (
                  <ContractCard
                    key={contract.id}
                    contract={contract}
                    staff={staff}
                    onClick={() => onOpenStaff(staff.id)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Archived */}
          {archived.length > 0 && (
            <section className="space-y-2">
              <SectionHeader
                tone="slate"
                icon={Archive}
                label="Recently archived"
                count={archived.length}
              />
              <div className="space-y-2">
                {archived.map(({ contract, staff }) => (
                  <ContractCard
                    key={contract.id}
                    contract={contract}
                    staff={staff}
                    onClick={() => onOpenStaff(staff.id)}
                    muted
                  />
                ))}
              </div>
            </section>
          )}

          {enriched.length === 0 && (
            <SoftCard hover={false}>
              <p className="text-sm text-gray-500 text-center py-6">
                No contracts on file yet.
              </p>
            </SoftCard>
          )}
            </div>
          </details>
        </>
      )}
    </div>
  );
}

/* ==================================================================
   CARD
   ================================================================== */
function ContractCard({
  contract,
  staff,
  onClick,
  accent,
  muted,
}: {
  contract: HrContract;
  staff: HrStaff;
  onClick: () => void;
  accent?: 'amber';
  muted?: boolean;
}) {
  // Ending-soon contracts (accent='amber') get a subtle amber border;
  // everything else uses the standard card border. The status story is
  // told by the badge on the right and the section header above.
  const border =
    accent === 'amber'
      ? 'border border-amber-200 dark:border-amber-900/40'
      : 'border border-gray-100 dark:border-gray-700';
  return (
    <div
      onClick={onClick}
      className={`rounded-2xl bg-white dark:bg-gray-800 ${border} px-4 py-3 hover:shadow-md transition-shadow cursor-pointer ${muted ? 'opacity-95' : ''}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-[var(--accent-link-dark,#93c5fd)] flex items-center justify-center text-sm font-bold shrink-0">
          {staffInitials(staff)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-gray-800 dark:text-gray-100 text-sm truncate">
              {staffFullName(staff)}
            </p>
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 font-semibold">
              v{contract.version}
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {TYPE_LABELS[contract.type]} · {staff.jobTitle}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {fmtDate(contract.startDate)} – {contract.endDate ? fmtDate(contract.endDate) : 'open'}
            {' · '}
            {endLabel(contract)}
          </p>
        </div>
        <span
          className={`text-xs font-medium px-2.5 py-1 rounded-lg whitespace-nowrap shrink-0 ${
            accent === 'amber'
              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
              : contract.active
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
          }`}
        >
          {accent === 'amber' ? 'Ending soon' : contract.active ? 'Active' : 'Archived'}
        </span>
        <FileText size={16} className="text-gray-400 shrink-0" />
      </div>
    </div>
  );
}

/* ==================================================================
   Section header + filter chip
   ================================================================== */
const TONE_TEXT: Record<'amber' | 'slate', string> = {
  amber: 'text-amber-700 dark:text-amber-300',
  slate: 'text-gray-500 dark:text-gray-400',
};

function SectionHeader({
  tone,
  icon: Icon,
  label,
  count,
}: {
  tone: 'amber' | 'slate';
  icon?: typeof AlertTriangle;
  label: string;
  count: number;
}) {
  return (
    <div className="flex items-center gap-2 px-1">
      {Icon && <Icon size={14} className={TONE_TEXT[tone]} />}
      <span className={`text-[11px] uppercase tracking-wider font-semibold ${TONE_TEXT[tone]}`}>
        {label}
      </span>
      <span className="text-[11px] text-gray-400">{count}</span>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
        active
          ? 'bg-[var(--accent-strong,#1b3a6b)] text-white'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
      }`}
    >
      {children}
    </button>
  );
}
